import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import config from 'src/shared/config';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { Link } from '../buttons';
import { QuestionDialog, useDialogManager } from '../dialog';
import { DropdownContent, DropdownHeader, DropdownItemBody, DropdownItemText, DropdownList, DropdownListItem } from '../dropdown';
import { SearchInput } from '../input';

export interface LanguageDropdownProps {
    isPopupVisible?: boolean;
    onItemClick?: () => void;
}

const languages = [
    { code: 'af', label: 'Afrikaans' },
    { code: 'sq', label: 'Albanian' },
    { code: 'am', label: 'Amharic' },
    { code: 'ar', label: 'Arabic' },
    { code: 'hy', label: 'Armenian' },
    { code: 'az', label: 'Azeri - Cyrillic' },
    { code: 'eu', label: 'Basque' },
    { code: 'bn', label: 'Bengali' },
    { code: 'bs', label: 'Bosnian' },
    { code: 'bg', label: 'Bulgarian' },
    { code: 'my', label: 'Burmese' },
    { code: 'zh-CN', label: 'Chinese - China' },
    { code: 'zh-TW', label: 'Chinese - Taiwan' },
    { code: 'hr', label: 'Croatian' },
    { code: 'cs', label: 'Czech' },
    { code: 'da', label: 'Danish' },
    { code: 'de', label: 'Deutsch' },
    { code: 'nl', label: 'Dutch' },
    { code: 'en-GB', label: 'English - United Kingdom' },
    { code: 'en-US', label: 'English - United States' },
    { code: 'es-ES', label: 'Español' },
    { code: 'et', label: 'Estonian' },
    { code: 'fa', label: 'Farsi - Persian' },
    { code: 'fil', label: 'Filipino' },
    { code: 'fi', label: 'Finnish' },
    { code: 'fr', label: 'Français' },
    { code: 'gl', label: 'Galician' },
    { code: 'ka', label: 'Georgian' },
    { code: 'el', label: 'Greek' },
    { code: 'gu', label: 'Gujarati' },
    { code: 'ha', label: 'Hausa' },
    { code: 'he', label: 'Hebrew' },
    { code: 'hi', label: 'Hindi' },
    { code: 'hu', label: 'Hungarian' },
    { code: 'id', label: 'Indonesian' },
    { code: 'it-IT', label: 'Italiano' },
    { code: 'ja', label: 'Japanese' },
    { code: 'kn', label: 'Kannada' },
    { code: 'kk', label: 'Kazakh' },
    { code: 'km', label: 'Khmer' },
    { code: 'ko', label: 'Korean' },
    { code: 'ky-KG', label: 'Kyrgyz - Cyrillic' },
    { code: 'lo', label: 'Lao' },
    { code: 'lv', label: 'Latvian' },
    { code: 'lt', label: 'Lithuanian' },
    { code: 'mk', label: 'Macedonia' },
    { code: 'ms', label: 'Malay' },
    { code: 'ml', label: 'Malayalam' },
    { code: 'mr', label: 'Marathi' },
    { code: 'mn', label: 'Mongolian' },
    { code: 'ne', label: 'Nepali' },
    { code: 'nb-NO', label: 'Norwegian' },
    { code: 'pl', label: 'Polskie' },
    { code: 'pt-PT', label: 'Português' },
    { code: 'pt-BR', label: 'Portuguese - Brazil' },
    { code: 'pa', label: 'Punjabi' },
    { code: 'ro-MO', label: 'Romanian' },
    { code: 'ru', label: 'Russian' },
    { code: 'sr', label: 'Serbian - Cyrillic' },
    { code: 'sd', label: 'Sindhi' },
    { code: 'si', label: 'Sinhala' },
    { code: 'sk', label: 'Slovak' },
    { code: 'sl', label: 'Slovenian' },
    { code: 'so', label: 'Somali' },
    { code: 'sw', label: 'Swahili' },
    { code: 'sv-SE', label: 'Swedish' },
    { code: 'tg', label: 'Tajik' },
    { code: 'ta', label: 'Tamil' },
    { code: 'tr', label: 'Turkish' },
    { code: 'uk', label: 'Ukrainian' },
    { code: 'ur', label: 'Urdu' },
    { code: 'uz-UZ', label: 'Uzbek - Cyrillic' },
    { code: 'vi', label: 'Vietnamese' },
    { code: 'cy', label: 'Welsh' },
    { code: 'xh', label: 'Xhosa' },
    { code: 'yi', label: 'Yiddish' },
    { code: 'zu', label: 'Zulu' }
] as const;

const LanguageSearchHeader = styled(DropdownHeader)`
    padding: 8px;

    > div {
        flex: 1 1 auto;
        min-width: 0;
    }
`;

const EmptyLanguageItem = styled.div`
    padding: 10px 16px;
    color: #6b7280;
    font-size: 13px;
    line-height: 18px;
`;

const LanguageDropdownContent = styled(DropdownContent)`
    height: 280px;
`;

const LanguageDropdown: FunctionComponent<LanguageDropdownProps> = (props) => {
    const { showDialog, closeDialog } = useDialogManager();
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (props.isPopupVisible === false) return;

        const focusFrame = window.requestAnimationFrame(() => {
            searchInputRef.current?.focus();
        });

        return () => window.cancelAnimationFrame(focusFrame);
    }, [props.isPopupVisible]);

    if (props.isPopupVisible === false) return null;

    const filter = search.trim().toLowerCase();
    const filteredLanguages = filter ? languages.filter((language) => `${language.label} ${language.code}`.toLowerCase().includes(filter)) : languages;

    const navigateToLanguage = (newLanguage: string) => {
        const currentUrl = new URL(window.location.href);
        const segments = currentUrl.pathname.split('/').filter(Boolean);
        const cultureSegments = [config.culture.toLowerCase(), config.culture.toLowerCase().split('-')[0]];
        const cultureSegmentIndex = segments.findIndex((segment) => cultureSegments.includes(segment.toLowerCase()));

        if (cultureSegmentIndex >= 0) {
            segments[cultureSegmentIndex] = newLanguage;
        } else {
            segments.unshift(newLanguage);
        }

        currentUrl.pathname = `/${segments.join('/')}`;
        const newUrl = currentUrl.toString();

        if (newLanguage !== 'en-GB' && newLanguage !== 'en-US') {
            const msgLanguage = (
                <span>
                    {T._('Please be aware that ', 'Admin')}
                    <strong>{T._('the Zakeke back-office is primarily developed in English, ', 'Admin')}</strong>
                    {T._(
                        'other languages may rely on automatic translations, which could result in some inaccuracies or unrefined translations. If you would like to modify these ',
                        'Admin'
                    )}
                    <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/4404019251090-Translations'>
                        {T._('translations', 'Admin')}
                    </Link>
                    {T._(', you can do so by navigating to Settings > Translations.', 'Admin')}
                </span>
            );

            showDialog(
                'viewerLanguage',
                <QuestionDialog
                    message={msgLanguage}
                    showCloseButton={false}
                    buttonYesLabel={T._('Ok', 'Admin')}
                    buttonNoLabel={T._('Cancel', 'Admin')}
                    onYesClick={() => {
                        window.location.href = newUrl;
                    }}
                    onNoClick={() => {
                        closeDialog('viewerLanguage');
                    }}
                />
            );
            return;
        }

        window.location.href = newUrl;
    };

    const handleLanguageClick = (newLanguage: string) => {
        props.onItemClick?.();
        navigateToLanguage(newLanguage);
    };

    return (
        <>
            <LanguageSearchHeader>
                <SearchInput
                    ref={searchInputRef}
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                    placeholder={T._('Search languages...', 'Admin')}
                />
            </LanguageSearchHeader>
            <LanguageDropdownContent>
                <DropdownList>
                    {filteredLanguages.map((language) => {
                        const active = config.culture.toLowerCase() === language.code.toLowerCase();

                        return (
                            <DropdownListItem key={language.code} type='button' $active={active} onClick={() => handleLanguageClick(language.code)}>
                                <DropdownItemBody>
                                    <DropdownItemText>{language.label}</DropdownItemText>
                                </DropdownItemBody>
                            </DropdownListItem>
                        );
                    })}
                    {filteredLanguages.length === 0 && <EmptyLanguageItem>{T._('No languages found', 'Admin')}</EmptyLanguageItem>}
                </DropdownList>
            </LanguageDropdownContent>
        </>
    );
};

export default LanguageDropdown;
